import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Loader2, Trophy, LayoutDashboard, Copy, Check, AlertTriangle, Swords } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchTournamentDeck } from '../lib/metaApi';
import { sendDeckCodeToRising } from '../services/risingLink';
import { getProxyImageUrl } from '../utils';

const GROUP_LABELS = {
  superHeroList: 'Ultra Heroes',
  superMonsterList: 'Kaiju',
  sceneList: 'Scenes',
  superMachineList: 'Machines',
};
const GROUP_ORDER = ['superHeroList', 'superMonsterList', 'superMachineList', 'sceneList'];

function ordinal(n) {
  if (n === null || n === undefined) return '—';
  const rules = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
  const suffix = rules[new Intl.PluralRules('en', { type: 'ordinal' }).select(n)] || 'th';
  return `${n}${suffix}`;
}

/** Catalog art goes through the image proxy; app-only cards fall back to their CDN art. */
function cardImage(card) {
  if (card.images?.full) return getProxyImageUrl(card.images.full);
  return card.fallbackImageUrl || null;
}

function CardCell({ card }) {
  const src = cardImage(card);
  return (
    <div className="relative">
      <div className="aspect-[5/7] rounded-md overflow-hidden bg-muted">
        {src ? (
          <img src={src} alt={card.name || card.number} loading="lazy"
            className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center p-1 text-[10px] text-center text-muted-foreground">
            {card.name || card.number}
          </div>
        )}
      </div>
      <span className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center tabular-nums">
        {card.count}
      </span>
      <p className="mt-1 text-[11px] leading-tight line-clamp-2" title={card.name || card.number}>
        {card.name || card.number}
      </p>
      <p className="text-[10px] font-mono text-muted-foreground">{card.number}</p>
    </div>
  );
}

export default function TournamentDeckView() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  const [deck, setDeck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sendingToRising, setSendingToRising] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchTournamentDeck(code, { lang: language });
        if (!cancelled) { setDeck(data); setError(null); }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code, language]);

  const groups = useMemo(() => {
    if (!deck) return [];
    return GROUP_ORDER
      .map((group) => ({
        group,
        label: GROUP_LABELS[group],
        cards: deck.cards.filter((c) => c.group === group),
      }))
      .filter((g) => g.cards.length > 0);
  }, [deck]);

  const handleCopy = async () => {
    if (!deck?.deckCode) return;
    await navigator.clipboard.writeText(deck.deckCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Hand this decklist to UCG Rising. The server already minted the deck code,
  // so we pass it through rather than re-encoding from the rendered card list —
  // that list is display-shaped (grouped, localized) and isn't the source of truth.
  const handleSendToRising = async () => {
    if (!deck?.deckCode || sendingToRising) return;
    setSendingToRising(true);
    try {
      const { url, opened, copied: didCopy } = await sendDeckCodeToRising(deck.deckCode);
      if (!opened) {
        // Popup blocked. The code is on the clipboard if the copy landed;
        // otherwise surface the URL so the deck is still reachable.
        window.alert(
          didCopy
            ? (t('common.deckCodeCopied') ||
               'Deck code copied — paste it into UCG Rising’s Import box')
            : `${t('common.sendToRisingHint') || 'Open this deck in UCG Rising'}\n\n${url}`
        );
      }
    } catch (err) {
      console.error('Send to Rising failed:', err);
    } finally {
      setSendingToRising(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !deck) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-xl font-semibold mb-2">
          {t('deckDatabase.notFound') || 'Decklist not found'}
        </h1>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button variant="outline" onClick={() => navigate('/decks/tournament')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('deckDatabase.backToList') || 'Back to the deck database'}
        </Button>
      </div>
    );
  }

  const best = deck.record?.bestResult || {};

  return (
    <div className="min-h-[calc(100vh-48px-40px)] bg-gradient-to-b from-background to-muted/30">
      <Helmet>
        <title>{`${deck.archetype || 'Tournament deck'} | Deck Database`}</title>
      </Helmet>

      <div className="bg-gradient-to-r from-brand to-brand-accent text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Button variant="ghost" size="sm"
            className="text-white hover:bg-white/15 hover:text-white -ml-2 mb-3"
            onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('deckDatabase.back') || 'Back'}
          </Button>
          {/* The pilot's own deck title is not shown and is no longer served: it
              is free text that routinely carries handles and personal references,
              and no sanitising rule covers arbitrary user input. The archetype
              label stands in for it. */}
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold">{deck.archetype}</h1>
            {deck.record?.bestRank === 1 && (
              <Badge className="gap-1 bg-white/20 hover:bg-white/20 border-transparent">
                <Trophy className="h-3 w-3" />
                {t('deckDatabase.eventWinner') || 'Event winner'}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Record */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              {t('deckDatabase.bestFinish') || 'Best finish'}
            </div>
            <div className="text-2xl font-bold tabular-nums">
              {ordinal(deck.record?.bestRank)}
              {best.field && <span className="text-base font-normal text-muted-foreground"> / {best.field}</span>}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              {t('deckDatabase.piloted') || 'Piloted'}
            </div>
            <div className="text-2xl font-bold tabular-nums">{deck.record?.entries ?? '—'}×</div>
            <div className="text-xs text-muted-foreground mt-1">
              {deck.record?.events} {deck.record?.events === 1 ? 'event' : 'events'}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              {t('deckDatabase.event') || 'Event'}
            </div>
            <div className="font-medium line-clamp-2" title={best.eventName}>{best.eventName || '—'}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {best.endDateText}{best.prefecture && ` · ${best.prefecture}`}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              {t('deckDatabase.cards') || 'Cards'}
            </div>
            <div className="text-2xl font-bold tabular-nums">{deck.counts?.total ?? '—'}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {deck.counts?.superHero} hero · {deck.counts?.superMonster} kaiju · {deck.counts?.scene} scene
            </div>
          </CardContent></Card>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {deck.shareUrl ? (
            <Button onClick={() => navigate(deck.shareUrl)}>
              <LayoutDashboard className="h-4 w-4 mr-2" />
              {t('deckDatabase.loadInBuilder') || 'Load in Deck Builder'}
            </Button>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground rounded-md border px-3 py-2">
              <AlertTriangle className="h-4 w-4" />
              {t('deckDatabase.cannotLoad') ||
                'This list can’t be loaded into the builder — it contains cards missing from the catalog'}
              {deck.uncatalogedCards?.length > 0 && (
                <span className="font-mono text-xs">({deck.uncatalogedCards.join(', ')})</span>
              )}
            </div>
          )}
          {/* Gated on deckCode for the same reason as the builder link: the server
              returns one only when every card resolved, so a list with uncataloged
              cards has nothing playable to hand over. */}
          {deck.deckCode && (
            <Button
              variant="outline"
              onClick={handleSendToRising}
              disabled={sendingToRising}
              title={t('common.sendToRisingHint')}
            >
              {sendingToRising
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Swords className="h-4 w-4 mr-2" />}
              {t('common.sendToRising') || 'Play on UCG Rising'}
            </Button>
          )}
          {deck.deckCode && (
            <Button variant="outline" onClick={handleCopy}>
              {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              {copied
                ? (t('deckDatabase.copied') || 'Copied')
                : (t('deckDatabase.copyCode') || 'Copy deck code')}
            </Button>
          )}
        </div>

        {/* Cards */}
        {groups.map(({ group, label, cards }) => (
          <section key={group}>
            <h2 className="text-lg font-semibold mb-3">
              {label}
              <span className="ml-2 text-sm font-normal text-muted-foreground tabular-nums">
                {cards.reduce((sum, c) => sum + c.count, 0)}
              </span>
            </h2>
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
              {cards.map((card) => <CardCell key={`${group}-${card.number}`} card={card} />)}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
